import { useCallback, useEffect, useRef, useState } from 'react'
import { ConfirmDialog, Modal } from '@/shared/components/ui'
import { useToast } from '@/shared/hooks/useToast'
import type { EntityMedia, EntityMediaUploadResult } from './entityMediaService'

export interface EntityMediaModalMessages {
  title: string
  attachedTitle: (count: number) => string
  empty: string
  loading: string
  newNameLabel: string
  newNamePlaceholder: string
  uploading: string
  dropzone: string
  hint: string
  types: {
    image: string
    video: string
    pdf?: string
    document?: string
    spreadsheet?: string
    other?: string
  }
  actions: {
    close: string
    confirm: string
    open: string
    favorite?: string
    editName: string
    remove: string
  }
  messages: {
    loadError: string
    uploadSuccess: string
    openError: string
    deleteTitle: string
    deleteConfirm: (name: string) => string
    deleteSuccess: string
    renameSuccess: string
    favoriteSuccess?: string
  }
}

export interface EntityMediaModalService {
  getMedia: (entityId: string) => Promise<EntityMedia[]>
  uploadMedia: (unitId: string, entityId: string, file: File, mediaName?: string) => Promise<EntityMediaUploadResult>
  getMediaUrl: (mediaPath: string) => Promise<string>
  updateMediaName: (mediaId: string, mediaName: string) => Promise<EntityMedia>
  setFavorite?: (entityId: string, mediaId: string) => Promise<EntityMedia[]>
  deleteMedia: (mediaId: string, mediaPath: string) => Promise<void>
}

interface EntityMediaModalProps {
  entityId: string
  unitId: string
  service: EntityMediaModalService
  messages: EntityMediaModalMessages
  accept?: string
  onClose: () => void
}

export function EntityMediaModal({
  entityId,
  unitId,
  service,
  messages,
  accept = 'image/*,video/mp4,video/quicktime,video/webm',
  onClose,
}: EntityMediaModalProps) {
  const toast = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [media, setMedia] = useState<EntityMedia[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMedia, setLoadingMedia] = useState(true)
  const [dragOver, setDragOver] = useState(false)
  const [mediaName, setMediaName] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [mediaToDelete, setMediaToDelete] = useState<EntityMedia | null>(null)
  const typeLabel = (item: EntityMedia) => messages.types[item.media_type] ?? item.media_type
  const isFavorite = (item: EntityMedia) => item.metadata?.favorite === true
  const typeIcon = (item: EntityMedia) => {
    if (item.media_type === 'video') {
      return (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      )
    }

    if (item.media_type === 'image') {
      return (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      )
    }

    return (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
    )
  }

  useEffect(() => {
    let active = true

    const loadMedia = async () => {
      try {
        const items = await service.getMedia(entityId)
        if (active) setMedia(items)
      } catch (error) {
        console.error('[EntityMediaModal] Failed to load media:', error)
        toast.error(messages.messages.loadError)
      } finally {
        if (active) setLoadingMedia(false)
      }
    }

    loadMedia()

    return () => {
      active = false
    }
  }, [entityId, messages.messages.loadError, service, toast])

  const handleFile = useCallback(
    async (file: File) => {
      setLoading(true)
      try {
        const result = await service.uploadMedia(unitId, entityId, file, mediaName || undefined)
        toast.success(messages.messages.uploadSuccess)
        if (service.setFavorite && media.length === 0) {
          const updated = await service.setFavorite(entityId, result.id)
          setMedia(updated)
        } else {
          const updated = await service.getMedia(entityId)
          setMedia(updated)
        }
        setMediaName('')
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        toast.error(message)
      } finally {
        setLoading(false)
      }
    },
    [entityId, media.length, mediaName, messages.messages.uploadSuccess, service, toast, unitId],
  )

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) handleFile(file)
    event.target.value = ''
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    setDragOver(false)
    const file = event.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  const handleView = async (item: EntityMedia) => {
    try {
      const url = await service.getMediaUrl(item.media_url)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch {
      toast.error(messages.messages.openError)
    }
  }

  const handleDelete = async () => {
    if (!mediaToDelete) return
    try {
      await service.deleteMedia(mediaToDelete.id, mediaToDelete.media_url)
      toast.success(messages.messages.deleteSuccess)
      setMedia((prev) => prev.filter((entry) => entry.id !== mediaToDelete.id))
      setMediaToDelete(null)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      toast.error(message)
    }
  }

  const handleEditName = async (item: EntityMedia) => {
    if (!editingName.trim()) {
      setEditingId(null)
      return
    }

    try {
      const updated = await service.updateMediaName(item.id, editingName.trim())
      toast.success(messages.messages.renameSuccess)
      setMedia((prev) => prev.map((entry) => (entry.id === item.id ? updated : entry)))
      setEditingId(null)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      toast.error(message)
    }
  }

  const handleSetFavorite = async (item: EntityMedia) => {
    if (!service.setFavorite || isFavorite(item)) return

    try {
      const updated = await service.setFavorite(entityId, item.id)
      setMedia(updated)
      toast.success(messages.messages.favoriteSuccess ?? messages.messages.renameSuccess)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      toast.error(message)
    }
  }

  const headerRight = (
    <div className="flex items-center gap-2">
      <button
        onClick={onClose}
        className="hidden sm:flex p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-600 dark:text-slate-400"
        title={messages.actions.close}
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <button
        onClick={onClose}
        className="p-1.5 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors text-emerald-600 dark:text-emerald-400"
        title={messages.actions.confirm}
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </button>
    </div>
  )

  return (
    <>
      <Modal isOpen onClose={onClose} title={messages.title} size="md" headerRight={headerRight}>
        <div className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
          {!loadingMedia && media.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                {messages.attachedTitle(media.length)}
              </h3>
              <div className="space-y-2">
                {media.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-900/30 border border-slate-200 dark:border-slate-700"
                  >
                    <svg className="h-5 w-5 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {typeIcon(item)}
                    </svg>

                    {editingId === item.id ? (
                      <input
                        type="text"
                        value={editingName}
                        onChange={(event) => setEditingName(event.target.value)}
                        onBlur={() => handleEditName(item)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') handleEditName(item)
                          if (event.key === 'Escape') setEditingId(null)
                        }}
                        autoFocus
                        className="flex-1 px-2 py-1 text-sm rounded border border-emerald-400 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                      />
                    ) : (
                      <div
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => handleView(item)}
                        title={messages.actions.open}
                      >
                        <p className="text-sm font-medium text-slate-900 dark:text-white truncate hover:underline">
                          {item.media_name}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {typeLabel(item)} - {new Date(item.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    )}

                    {editingId !== item.id && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {service.setFavorite && (
                          <button
                            onClick={() => handleSetFavorite(item)}
                            className={`p-1.5 rounded transition-colors ${
                              isFavorite(item)
                                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-300'
                                : 'hover:bg-amber-100 dark:hover:bg-amber-900/30 text-slate-400 hover:text-amber-600 dark:hover:text-amber-300'
                            }`}
                            title={messages.actions.favorite}
                            aria-pressed={isFavorite(item)}
                          >
                            <svg className="h-4 w-4" fill={isFavorite(item) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.563.563 0 00-.182-.557L3.04 10.385a.562.562 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345l2.125-5.111z" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => handleView(item)}
                          className="p-1.5 rounded hover:bg-emerald-100 dark:hover:bg-emerald-800 transition-colors text-emerald-700 dark:text-emerald-300"
                          title={messages.actions.open}
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </button>
                        <button
                          onClick={() => {
                            setEditingId(item.id)
                            setEditingName(item.media_name)
                          }}
                          className="p-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors text-blue-600 dark:text-blue-400"
                          title={messages.actions.editName}
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setMediaToDelete(item)}
                          className="p-1.5 rounded hover:bg-rose-100 dark:hover:bg-rose-900/30 transition-colors text-rose-600 dark:text-rose-400"
                          title={messages.actions.remove}
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!loadingMedia && media.length === 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-400 py-2">
              {messages.empty}
            </p>
          )}

          {loadingMedia && (
            <p className="text-sm text-slate-500 dark:text-slate-400">{messages.loading}</p>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {messages.newNameLabel}
            </label>
            <input
              type="text"
              value={mediaName}
              onChange={(event) => setMediaName(event.target.value)}
              placeholder={messages.newNamePlaceholder}
              disabled={loading}
              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50"
            />
          </div>

          <div
            onDragOver={(event) => {
              event.preventDefault()
              setDragOver(true)
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`relative rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
              dragOver
                ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 cursor-pointer'
                : 'border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 cursor-pointer'
            } ${loading ? 'pointer-events-none opacity-60' : ''}`}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleInputChange}
              disabled={loading}
              accept={accept}
              className="hidden"
            />
            <svg className="h-10 w-10 mx-auto mb-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {loading ? messages.uploading : messages.dropzone}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {messages.hint}
            </p>
          </div>
        </div>
      </Modal>
      <ConfirmDialog
        isOpen={!!mediaToDelete}
        onClose={() => setMediaToDelete(null)}
        onConfirm={handleDelete}
        title={messages.messages.deleteTitle}
        message={messages.messages.deleteConfirm(mediaToDelete?.media_name ?? '')}
        confirmText={messages.actions.remove}
        type="danger"
      />
    </>
  )
}
